import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a <theme-drawer>.
 *
 * Dialog lifecycle is owned by <theme-drawer>.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  #drawer = null;

  /** @type {HTMLDialogElement | null} */
  #drawerDialog = null;

  /** @type {number | null} */
  #stickyRafId = null;

  /** @type {HTMLElement | null} */
  #installmentsCta = null;

  /**
   * Stable listener.
   * Prevents duplicate click handlers from accumulating
   * after repeated cart drawer openings.
   */
  #handleInstallmentsClick = () => {
    this.#drawer?.close();
  };

  connectedCallback() {
    super.connectedCallback();

    this.#drawer =
      /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
        this.closest('theme-drawer')
      );

    this.#drawerDialog = this.closest('dialog');

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#drawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    /*
     * The restore path may set [open] before this JS loads.
     */
    if (this.#drawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#drawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    if (this.#stickyRafId !== null) {
      cancelAnimationFrame(this.#stickyRafId);
      this.#stickyRafId = null;
    }

    if (this.#installmentsCta) {
      this.#installmentsCta.removeEventListener(
        'click',
        this.#handleInstallmentsClick
      );

      this.#installmentsCta = null;
    }

    this.#drawer = null;
    this.#drawerDialog = null;
  }

  /**
   * Handles cart drawer opening.
   */
  #handleDrawerOpen = () => {
    this.#scheduleStickyStateUpdate();

    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        const nextCta =
          this.querySelector('shopify-payment-terms')
            ?.shadowRoot
            ?.querySelector('#shopify-installments-cta');

        /*
         * Cart content may be re-rendered,
         * so the Shopify shadow DOM CTA can be replaced.
         */
        if (this.#installmentsCta !== nextCta) {
          this.#installmentsCta?.removeEventListener(
            'click',
            this.#handleInstallmentsClick
          );

          this.#installmentsCta =
            nextCta instanceof HTMLElement
              ? nextCta
              : null;
        }

        if (!this.#installmentsCta) return;

        /*
         * Remove first to guarantee there is only one listener.
         */
        this.#installmentsCta.removeEventListener(
          'click',
          this.#handleInstallmentsClick
        );

        this.#installmentsCta.addEventListener(
          'click',
          this.#handleInstallmentsClick,
          { once: true }
        );
      });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const drawer = this.#drawer;

    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !drawer?.isOpen;

    /*
     * When add-to-cart comes from Quick Add modal,
     * wait until that modal closes before opening cart.
     */
    const sourceModal =
      /** @type {HTMLDialogElement | null} */ (
        event.target instanceof Element
          ? event.target.closest('dialog:modal')
          : null
      );

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      drawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        if (
          !shouldAutoOpen ||
          detail?.didError
        ) {
          this.#scheduleStickyStateUpdate();
          return;
        }

        const openAndSettle = () => {
          if (!drawer?.isOpen) {
            drawer?.open();
          }

          this.#scheduleStickyStateUpdate();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Prevent several cart events in the same frame
   * from forcing repeated layout measurements.
   */
  #scheduleStickyStateUpdate() {
    if (this.#stickyRafId !== null) return;

    this.#stickyRafId =
      requestAnimationFrame(() => {
        this.#stickyRafId = null;
        this.#updateStickyState();
      });
  }

  #isCartEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer--empty')
    );
  }

  #updateStickyState() {
    const dialog = this.#drawerDialog;

    if (!dialog) return;

    const content =
      dialog.querySelector(
        '.cart-drawer__content'
      );

    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );

    if (!content || !summary) {
      this.#setStickyState(dialog, false);
      return;
    }

    /*
     * Keep the original measurement logic so visual
     * sticky behavior remains exactly the same.
     *
     * Reads happen together before the DOM write.
     */
    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    if (drawerHeight <= 0) return;

    const ratio =
      summaryHeight / drawerHeight;

    this.#setStickyState(
      dialog,
      ratio <= this.#summaryThreshold
    );
  }

  /**
   * Avoid writing the same DOM attribute repeatedly.
   *
   * @param {HTMLDialogElement} dialog
   * @param {boolean} sticky
   */
  #setStickyState(dialog, sticky) {
    const value =
      sticky ? 'true' : 'false';

    if (
      dialog.getAttribute(
        'cart-summary-sticky'
      ) === value
    ) {
      return;
    }

    dialog.setAttribute(
      'cart-summary-sticky',
      value
    );
  }
}

if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}